import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pill,
  Edit,
  Eye,
  MoreHorizontal,
  Trash2,
  Package,
  Receipt,
} from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { getDoctorName } from "@/utils/dataLookups";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import PrescriptionInvoicesModal from "./PrescriptionInvoicesModal";

const dispenseStatusColors = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  partially_dispensed: "bg-blue-100 text-blue-700 border-blue-200",
  fully_dispensed: "bg-green-100 text-green-700 border-green-200",
  external: "bg-purple-100 text-purple-700 border-purple-200",
};

export default function PrescriptionList({
  prescriptions,
  doctors = [],
  isLoading,
  onSelectPrescription,
  onEditPrescription,
  onDeletePrescription,
  onDispensePrescription,
  isPatientView = false,
}) {
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedPrescriptionForInvoices, setSelectedPrescriptionForInvoices] = useState(null);

  // Fetch invoices for selected prescription
  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ["prescription-invoices", selectedPrescriptionForInvoices?.id],
    queryFn: () =>
      apiClient.get(ENDPOINTS.PRESCRIPTIONS.INVOICES(selectedPrescriptionForInvoices.id)),
    enabled: !!selectedPrescriptionForInvoices && showInvoicesModal,
  });

  const handleShowInvoices = (prescription, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedPrescriptionForInvoices(prescription);
    setShowInvoicesModal(true);
  };

  const handleCloseInvoicesModal = () => {
    setShowInvoicesModal(false);
    setSelectedPrescriptionForInvoices(null);
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white/60 backdrop-blur-sm shadow-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Patient</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Medications</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-4 w-32 bg-slate-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-slate-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-28 bg-slate-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-slate-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-slate-200 animate-pulse rounded-full" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-slate-200 animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (prescriptions.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white/60 backdrop-blur-sm shadow-lg p-12 text-center">
        <Pill className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No Prescriptions Found
        </h3>
        <p className="text-slate-600">
          Create your first prescription to get started
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white/60 backdrop-blur-sm shadow-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/50">
            <TableHead className="font-semibold">Patient</TableHead>
            <TableHead className="font-semibold">Date</TableHead>
            <TableHead className="font-semibold">Doctor</TableHead>
            <TableHead className="font-semibold">Medications</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            <TableHead className="w-[80px] font-semibold text-center">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prescriptions.map((prescription) => (
            <TableRow
              key={prescription.id}
              className="hover:bg-slate-50/50 cursor-pointer transition-colors"
              onClick={() => onSelectPrescription(prescription)}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Pill className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-slate-900">
                    {prescription.patientName || prescription.patientEmail || prescription.patientId}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-slate-600">
                {formatDate(prescription.prescriptionDate, "MMM d, yyyy")}
              </TableCell>
              <TableCell className="text-slate-600">
                {getDoctorName(prescription.doctorId, doctors)}
              </TableCell>
              <TableCell className="text-slate-600">
                <div className="flex items-center gap-3">
                  <div>
                    <span className="font-semibold text-slate-900">
                      {prescription.medications?.length || 0}
                    </span>{" "}
                    item{prescription.medications?.length !== 1 ? "s" : ""}
                  </div>
                  {!isPatientView && prescription.invoiceCount > 0 && (
                    <Badge
                      className="bg-green-50 text-green-700 border-green-200 gap-1 text-xs cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={(e) => handleShowInvoices(prescription, e)}
                      title="Click to view invoices"
                    >
                      <Receipt className="w-3 h-3" />
                      {prescription.invoiceCount}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  className={`${dispenseStatusColors[prescription.dispenseStatus]} border font-medium`}
                >
                  {prescription.dispenseStatus?.replace(/_/g, " ")}
                </Badge>
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onSelectPrescription(prescription)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      <span>View</span>
                    </DropdownMenuItem>
                    {!isPatientView && onEditPrescription && (
                      <DropdownMenuItem
                        onClick={() => onEditPrescription(prescription)}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        <span>Edit</span>
                      </DropdownMenuItem>
                    )}
                    {!isPatientView && onDispensePrescription && (
                      <DropdownMenuItem
                        onClick={() => onDispensePrescription(prescription)}
                      >
                        <Package className="mr-2 h-4 w-4" />
                        <span>Dispense</span>
                      </DropdownMenuItem>
                    )}
                    {!isPatientView && onDeletePrescription && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDeletePrescription(prescription.id)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <PrescriptionInvoicesModal
        isOpen={showInvoicesModal}
        onClose={handleCloseInvoicesModal}
        invoices={invoices}
        isLoading={isLoadingInvoices}
        patientName={selectedPrescriptionForInvoices?.patientName || selectedPrescriptionForInvoices?.patientEmail}
      />
    </div>
  );
}
